import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { isNullish } from 'remeda';
import { CREATE_CONVERSATION_ASSIGNEE_GROUP_MUTATION } from 'src/graphql/entities/conversation-assignee-group/conversation-assignee-group.operations';
import type {
  ConversationAssigneeGroupsCacheManager,
  CreateConversationAssigneeGroupInput,
  CreateConversationAssigneeGroupResponse,
} from 'src/graphql/entities/conversation-assignee-group/conversation-assignee-group.types';

export const useCreateConversationAssigneeGroup = (
  cacheManager: ConversationAssigneeGroupsCacheManager,
) => {
  const [mutate, { loading }] = useMutation<CreateConversationAssigneeGroupResponse>(
    CREATE_CONVERSATION_ASSIGNEE_GROUP_MUTATION,
    {
      update(_, { data }) {
        const created = data?.createConversationAssigneeGroup?.[0];
        if (isNullish(created?.partnerId)) return;

        cacheManager.update((existing) => {
          existing.getConversationAssigneeGroup.records.push(created);
        });
        cacheManager.client.cache.evict({
          id: 'ROOT_QUERY',
          fieldName: 'getEnabledConversationAssigneeGroups',
        });
        cacheManager.client.cache.gc();
      },
    },
  );

  const createWithOptimisticResponse = useCallback(
    (input: CreateConversationAssigneeGroupInput) => {
      const tempId = `temp-${crypto.randomUUID()}`;
      return mutate({
        variables: input,
        optimisticResponse: {
          createConversationAssigneeGroup: [
            {
              __typename: 'ConversationAssigneeGroup',
              id: tempId,
              conversationAssigneeGroupId: tempId,
              partnerId: input.partnerId,
              name: input.name,
              description: input.description ?? '',
              icon: input.icon ?? '',
            },
          ],
        },
      });
    },
    [mutate],
  );

  return {
    createConversationAssigneeGroup: createWithOptimisticResponse,
    loading,
  };
};
